import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authService, clearPendingVerificationContext, getPendingVerificationContext, getPostAuthRoute, setTokens, setUserProfile } from '../services/auth.service';
import AuthLayout from '../components/auth/AuthLayout';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Mock role detection (should come from auth state or navigation state)
  const pendingContext = getPendingVerificationContext();
  const userRole = location.state?.role || pendingContext.role || 'RENTER';
  const customerId = location.state?.customerId || pendingContext.customerId;
  const verificationPurpose = location.state?.purpose || pendingContext.purpose || 'signup';

  useEffect(() => {
    if (customerId) {
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      navigate('/account-verification', { replace: true });
      return;
    }

    navigate('/login', { replace: true });
  }, [customerId, navigate]);

  const handleChange = (index, value) => {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    const newOtp = [...otp];

    if (digits.length === 1) {
      newOtp[index] = digits;
      setOtp(newOtp);
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }

    // Support typing/pasting multiple digits at once (desktop paste or mobile one-time-code autofill)
    let nextIndex = index;
    for (const digit of digits) {
      if (nextIndex > 5) break;
      newOtp[nextIndex] = digit;
      nextIndex += 1;
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(nextIndex, 5)]?.focus();
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    const pasted = e.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '');
    if (!digits) return;

    const newOtp = [...otp];
    let nextIndex = index;
    for (const digit of digits) {
      if (nextIndex > 5) break;
      newOtp[nextIndex] = digit;
      nextIndex += 1;
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(nextIndex, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.join('').length < 6) return;

    setLoading(true);
    setError('');

    try {
      const otpValue = otp.join('');
      if (!customerId) {
        setError('Missing customer context. Please login again.');
        setLoading(false);
        return;
      }

      const response = verificationPurpose === 'login_2fa'
        ? await authService.verifyLoginOtp(customerId, otpValue)
        : await authService.verifyOtp(customerId, otpValue);

      if (response.action === 1) {
        setTokens(response.token, response.refreshToken);
        clearPendingVerificationContext();
        setUserProfile(response);
        navigate(getPostAuthRoute({ ...response, customerType: response.customerType || userRole }), { replace: true });
      } else {
        setError(response.message || 'Verification failed.');
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>

          <div className="mb-6 text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Two-Step Verification</h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              {verificationPurpose === 'login_2fa'
                ? "We've sent a 6-digit login verification code to your registered email address and phone number."
                : "We've sent a 6-digit verification code to your registered email address and phone number."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium mb-4">
                {error}
              </div>
            )}
            <div className="flex justify-between gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={(e) => handlePaste(index, e)}
                  onFocus={(e) => e.target.select()}
                  disabled={loading}
                  className="min-w-0 w-full flex-1 h-12 sm:h-14 text-center text-2xl font-bold rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all bg-white disabled:opacity-50"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || otp.join('').length < 6}
              className="w-full bg-brand-orange-500 text-white py-3 rounded-xl font-bold hover:bg-brand-orange-600 transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>

          <div className="mt-8 text-center" style={{ visibility: loading ? 'hidden' : 'visible' }}>
            <p className="text-sm text-gray-500 mb-6">
              Didn't receive the code? <button
                type="button"
                onClick={async () => {
                  try {
                    setError('');
                    if (verificationPurpose === 'login_2fa') {
                      await authService.resendLoginOtp(customerId);
                    } else {
                      await authService.resendOtp(customerId);
                    }
                  } catch (err) {
                    setError(err.message || 'Failed to resend code.');
                  }
                }}
                className="font-bold text-brand-black hover:underline"
              >Resend</button>
            </p>

            <Link to="/login" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-brand-black transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to Login
            </Link>
          </div>
    </AuthLayout>
  );
};

export default OTPVerification;
